import { useState, useCallback, useRef, TouchEvent } from 'react';

export type SheetState = 'collapsed' | 'half' | 'full';

export interface SheetHeights {
  collapsed: number;
  half: number;
  full: number;
}

const DEFAULT_SHEET_HEIGHTS: SheetHeights = {
  collapsed: 18,   // Peek - just show header
  half: 45,        // 45% of screen
  full: 90,        // 90% of screen
};

export function useBottomSheet(
  initialState: SheetState = 'collapsed',
  customHeights?: SheetHeights
) {
  const SHEET_HEIGHTS = customHeights || DEFAULT_SHEET_HEIGHTS;
  const [sheetState, setSheetState] = useState<SheetState>(initialState);
  const [isDragging, setIsDragging] = useState(false);
  const [dragHeight, setDragHeight] = useState<number>(SHEET_HEIGHTS[initialState]);
  const startY = useRef(0);
  const startHeight = useRef(SHEET_HEIGHTS[initialState]);

  const expand = useCallback(() => {
    setSheetState('full');
    setDragHeight(SHEET_HEIGHTS.full);
  }, []);

  const collapse = useCallback(() => {
    setSheetState('collapsed');
    setDragHeight(SHEET_HEIGHTS.collapsed);
  }, []);

  const setHalf = useCallback(() => {
    setSheetState('half');
    setDragHeight(SHEET_HEIGHTS.half);
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    startY.current = e.touches[0].clientY;
    startHeight.current = SHEET_HEIGHTS[sheetState];
    setIsDragging(true);
  }, [sheetState]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const deltaY = startY.current - currentY; // Positive = swipe up, Negative = swipe down
    const windowHeight = window.innerHeight;
    
    // Calculate new height as percentage
    const deltaPercent = (deltaY / windowHeight) * 100;
    const newHeight = startHeight.current + deltaPercent;
    
    // Clamp between min and max
    const clampedHeight = Math.min(90, Math.max(18, newHeight));
    setDragHeight(clampedHeight);
    
    // Prevent default to avoid page scroll during drag
    e.preventDefault();
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    
    // Snap to nearest state based on current drag height
    const currentDragHeight = dragHeight;
    
    // Find closest snap point
    if (currentDragHeight < (SHEET_HEIGHTS.collapsed + SHEET_HEIGHTS.half) / 2) {
      // Closer to collapsed
      setSheetState('collapsed');
      setDragHeight(SHEET_HEIGHTS.collapsed);
    } else if (currentDragHeight < (SHEET_HEIGHTS.half + SHEET_HEIGHTS.full) / 2) {
      // Closer to half
      setSheetState('half');
      setDragHeight(SHEET_HEIGHTS.half);
    } else {
      // Closer to full
      setSheetState('full');
      setDragHeight(SHEET_HEIGHTS.full);
    }
  }, [dragHeight]);

  const getSheetHeight = useCallback(() => {
    return isDragging ? dragHeight : SHEET_HEIGHTS[sheetState];
  }, [isDragging, dragHeight, sheetState]);

  const dragHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };

  return {
    sheetState,
    sheetHeight: getSheetHeight(),
    isDragging,
    expand,
    collapse,
    setHalf,
    dragHandlers,
  };
}

